/**
 * Centralized API utility
 * 
 * This module provides a configured axios instance with:
 * - Automatic auth token injection
 * - Base URL configuration
 * - Request/response interceptors
 * - Automatic token refresh on 401 errors
 * 
 * Usage:
 *   import api from '../utils/api';
 *   const response = await api.get('/tasks');
 *   const data = await api.post('/tasks', { name: 'New Task' });
 * 
 * For non-authenticated requests (login, register, public):
 *   import axios from 'axios';
 *   import { API_BASE_URL } from '../utils/api';
 *   await axios.post(`${API_BASE_URL}/api/auth/login`, credentials);
 */

import axios from 'axios';

const normalizeBackendUrl = (rawUrl) => {
  const fallback = 'http://localhost:8001';
  if (!rawUrl || typeof rawUrl !== 'string') return fallback;

  const trimmed = rawUrl.trim();

  // Browser mixed-content safety: if app is on HTTPS, force API URL to HTTPS too.
  // Prevents blocked XHR when env is accidentally set to http://...
  if (typeof window !== 'undefined' && window.location?.protocol === 'https:' && /^http:\/\//i.test(trimmed)) {
    return trimmed.replace(/^http:/i, 'https:');
  }

  // Extra prod guardrail for projectathena domains
  if (/^http:\/\/([\w.-]*\.)?projectathena\.us(?::\d+)?$/i.test(trimmed)) {
    return trimmed.replace(/^http:/i, 'https:');
  }

  return trimmed;
};

// Single source of truth for API URL
const API_BASE_URL = normalizeBackendUrl(process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001');

// Create axios instance with defaults
const api = axios.create({
  baseURL: `${API_BASE_URL}/api`,
  timeout: 30000 // 30 second timeout
});

// Track if we're currently refreshing to avoid multiple refresh attempts
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// Request interceptor - automatically add auth token
api.interceptors.request.use(
  (config) => {
    config.headers = config.headers || {};

    // Allow browser to set proper multipart boundary for FormData
    if (config.data instanceof FormData) {
      if (config.headers['Content-Type']) {
        delete config.headers['Content-Type'];
      }
    } else if (!config.headers['Content-Type'] && config.data !== undefined) {
      config.headers['Content-Type'] = 'application/json';
    }

    // Only add access_token if Authorization header is not already set
    if (!config.headers.Authorization) {
      const token = localStorage.getItem('access_token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Guard so a burst of parallel 401s triggers a single logout redirect instead
// of a reload storm (each redirect below is a full page load).
let logoutRedirectInProgress = false;

const forceLogout = () => {
  localStorage.removeItem('access_token');
  localStorage.removeItem('refresh_token');
  localStorage.removeItem('session_id');

  const path = window.location.pathname || '/';
  const isSuperAdminPath = path.startsWith('/super-admin');
  const isPortalPath = path.startsWith('/portal/');
  const isPublicPricingSignupPath = (
    path.startsWith('/pricing') ||
    path.startsWith('/signup') ||
    path.startsWith('/reactivate-account')
  );

  // Keep super-admin auth flow isolated from main app login flow.
  // Clearing the super-admin keys is essential: leaving them behind makes the
  // dashboard remount after the redirect, 401 again, and redirect again — an
  // infinite reload/flash loop between the dashboard and the login screen.
  if (isSuperAdminPath) {
    localStorage.removeItem('super_admin_token');
    localStorage.removeItem('super_admin_info');
    if (logoutRedirectInProgress) return;
    logoutRedirectInProgress = true;
    window.location.href = '/super-admin?session_expired=true';
    return;
  }

  // Keep client portal flow isolated from main app login flow
  if (isPortalPath) {
    const match = path.match(/^\/portal\/([^\/]+)/);
    if (match?.[1]) {
      if (logoutRedirectInProgress) return;
      logoutRedirectInProgress = true;
      window.location.href = `/portal/${match[1]}?session_expired=true`;
      return;
    }
  }

  // Pricing/signup/reactivation pages are public; do not bounce to app login
  if (isPublicPricingSignupPath) {
    return;
  }

  if (!path.includes('/login') && !path.includes('/reset-password')) {
    if (logoutRedirectInProgress) return;
    logoutRedirectInProgress = true;
    window.location.href = '/?session_expired=true';
  }
};

const isAuthFailure403 = (error) => {
  if (error?.response?.status !== 403) return false;
  const data = error.response.data || {};
  const blob = JSON.stringify(data).toLowerCase();

  return (
    blob.includes('not authenticated') ||
    blob.includes('authentication required') ||
    blob.includes('invalid token') ||
    blob.includes('token expired') ||
    blob.includes('jwt')
  );
};

// Response interceptor - handle common errors and token refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config || {};

    // If error is 401 and we haven't already tried to refresh
    if (error.response?.status === 401 && !originalRequest._retry) {
      // Don't try to refresh for auth endpoints
      if (originalRequest.url?.includes('/auth/login') ||
          originalRequest.url?.includes('/auth/register') ||
          originalRequest.url?.includes('/auth/refresh')) {
        return Promise.reject(error);
      }

      // Super-admin endpoints use their own token — the main-app refresh flow
      // can't renew it. End the super-admin session once instead of looping.
      if (originalRequest.url?.includes('/super-admin')) {
        if (!originalRequest.url.includes('/super-admin/auth/') &&
            (window.location.pathname || '').startsWith('/super-admin')) {
          forceLogout();
        }
        return Promise.reject(error);
      }

      // Check if we have a refresh token
      const refreshToken = localStorage.getItem('refresh_token');
      if (!refreshToken) {
        forceLogout();
        return Promise.reject(error);
      }

      if (isRefreshing) {
        // If we're already refreshing, queue this request
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(token => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return api(originalRequest);
        }).catch(err => {
          return Promise.reject(err);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        // Try to refresh the token
        const response = await axios.post(`${API_BASE_URL}/api/auth/refresh`, {
          refresh_token: refreshToken
        });

        if (response.data.success) {
          const newAccessToken = response.data.access_token;
          localStorage.setItem('access_token', newAccessToken);
          
          // Process queued requests
          processQueue(null, newAccessToken);
          
          // Retry original request
          originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
          return api(originalRequest);
        }
      } catch (refreshError) {
        // Refresh failed - clear tokens and redirect to login
        processQueue(refreshError, null);
        forceLogout();
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    // Force logout for explicit auth-related 403s in current session
    if (isAuthFailure403(error)) {
      forceLogout();
      return Promise.reject(error);
    }

    // Log errors for debugging (can be removed in production)
    if (error.response) {
      console.error(`API Error ${error.response.status}:`, error.response.data);
    } else if (error.request) {
      console.error('API Error: No response received', error.request);
    } else {
      console.error('API Error:', error.message);
    }
    return Promise.reject(error);
  }
);

// Export both the instance and base URL (for special cases like WebSockets)
export default api;
export { API_BASE_URL };
